import * as Sentry from "@sentry/react";
import { useRouter } from "@tanstack/react-router";
import { useLayoutEffect, useEffectEvent, useState } from "react";
import { useIntl } from "react-intl";

import { isWeb } from "@/app/platform";
import { Button } from "@/components/Button/Button";
import { Image } from "@/components/Image/Image";
import { LogInForm } from "@/components/Web/LogInForm";
import { showSnackbar } from "@/contexts/SnackbarContext";
import type { SignInError } from "@/errors/SignInError";
import { isProduction } from "@/utils/constants";

const TryAgainButton = () => {
  const intl = useIntl();

  const [isNavigating, setIsNavigating] = useState(false);
  const { invalidate } = useRouter();

  return (
    <Button
      onClick={async () => {
        setIsNavigating(true);
        try {
          await invalidate();
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          setIsNavigating(false);
        }
      }}
      loading={isNavigating}
      variant="contained"
      color="primary"
    >
      {intl.formatMessage({ id: "welcome.button" })}
    </Button>
  );
};

type Props = {
  error: SignInError;
};

export const SignInErrorMessage = ({ error }: Props) => {
  const intl = useIntl();

  const showError = useEffectEvent(() => {
    showSnackbar({
      content:
        error.cause === "cancelled"
          ? intl.formatMessage({ id: "welcome.loginCancelled" })
          : intl.formatMessage({ id: "welcome.errorMessage" }),
      type: "error",
    });
  });

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      showError();
    }, 200);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center p-4 text-center">
      <div className="-mt-20 mb-20 h-18 w-40">
        <Image src="/rahallogo.svg" aria-hidden />
      </div>
      <h1 className="text-heading-h1">
        {intl.formatMessage({ id: "welcome.title" })}
      </h1>
      <p className="mb-6">{intl.formatMessage({ id: "welcome.body" })}</p>

      {isWeb && !isProduction ? <LogInForm /> : <TryAgainButton />}
    </div>
  );
};
